import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { fileUrl } from "../lib/api";
import { Radio, Eye, ChevronLeft, RotateCw } from "lucide-react";
import VerifiedBadge from "../components/VerifiedBadge";
import UserGem from "../components/UserGem";
import SmartImage from "../components/SmartImage";

function LiveCard({ s, onOpen }) {
  return (
    <button
      onClick={() => onOpen(s)}
      className="group relative aspect-[3/4] rounded-3xl overflow-hidden bg-zinc-900 border border-white/10 hover:border-pink-400/50 transition-all text-left"
      data-testid={`live-card-${s.stream_id}`}
    >
      {s.vendor_avatar ? (
        <SmartImage
          src={fileUrl(s.vendor_avatar)}
          alt={s.vendor_name}
          mode="smart_auto_fit"
          className="absolute inset-0 w-full h-full group-hover:scale-105 transition-transform duration-500"
          testid={`live-card-image-${s.stream_id}`}
        />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-pink-600 to-cyan-600" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />

      <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] font-black uppercase tracking-widest" data-testid={`live-badge-${s.stream_id}`}>
        <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> LIVE
      </div>
      <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur text-white text-[10px] font-bold">
        <Eye className="w-3 h-3" /> {s.viewer_count || 0}
      </div>

      <div className="absolute bottom-0 left-0 right-0 p-3 space-y-1">
        <p className="text-xs text-zinc-300 line-clamp-1">{s.title || "Live now"}</p>
        <p className="text-sm font-black truncate inline-flex items-center gap-1">
          @{s.vendor_name}
          <VerifiedBadge verified={s.vendor_verified} size="xs" />
          <UserGem userId={s.vendor_id} size="xs" />
        </p>
      </div>
    </button>
  );
}

export default function Live() {
  const navigate = useNavigate();
  const [streams, setStreams] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/streams/live");
      setStreams(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const i = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(i);
  }, [load]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-5 space-y-5 pb-24" data-testid="live-page">
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
          aria-label="Back"
          data-testid="live-back"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-3xl font-black tracking-tighter inline-flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-red-500 text-white text-xs font-black uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> Live
            </span>
            Vendors
          </h1>
          <p className="text-sm text-zinc-400">Hop into a vendor's live drop right now.</p>
        </div>
        <button
          onClick={load}
          className="h-9 w-9 grid place-items-center rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-zinc-300"
          data-testid="live-refresh"
        >
          <RotateCw className="w-4 h-4" />
        </button>
      </div>

      {loading && (
        <div className="grid place-items-center py-16">
          <div className="w-10 h-10 rounded-full border-2 border-pink-500/40 border-t-pink-500 animate-spin" />
        </div>
      )}

      {!loading && streams.length === 0 && (
        <div className="text-center py-20 text-zinc-400" data-testid="live-empty">
          <Radio className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-bold">No one's live right now.</p>
          <p className="text-sm mt-1">Follow your favourite vendors to get notified when they go live.</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="live-grid">
        {streams.map((s) => (
          <LiveCard key={s.stream_id} s={s} onOpen={(s) => navigate(`/live/${s.stream_id}`)} />
        ))}
      </div>
    </div>
  );
}
